import crawlPolicyData from "../data/crawl-policy-data.json";
import { LEGACY_CRAWL_CANONICAL_PATHS } from "./canonical-redirects";
import { PRIVATE_CRAWL_PREFIXES } from "./private-route-policy";

export { LEGACY_CRAWL_CANONICAL_PATHS } from "./canonical-redirects";
const PUBLIC_NOINDEX_PATHS = new Set(crawlPolicyData.publicNoindexExactPaths);
const PUBLIC_NOINDEX_PATTERNS = crawlPolicyData.publicNoindexPathPatterns.map(
  (pattern) => new RegExp(pattern)
);

// Thin generated-detail prefixes that should be served but kept noindex,follow +
// out of the sitemap. Read ONLY through the Indexability Contract
// (`isThinNoindexPath` in src/lib/indexability.ts) so the sitemap filter and the
// page's robots header can never disagree.
//
// Empty as of the 2026-06-15 demand-led promotion: baby-names, compatibility, and
// lal-kitab-remedies detail pages each earn real GSC demand (40K / 250K / 0.6K
// impr/28d) and are already canonical + in DERIVED_SPECS, so suppressing them was
// destroying traffic. Add a prefix here ONLY for a cluster that is genuinely thin
// AND earns ~no impressions (verify against gsc_pages first) — that single edit
// then removes it from the sitemap AND stamps noindex, in lockstep.
export const WEAK_INDEXABLE_VALUE_DETAIL_PREFIXES: readonly string[] =
  crawlPolicyData.weakIndexableValueDetailPrefixes;

const QUERY_CANONICAL_EXEMPT_PREFIXES = [
  "/api",
  "/cart",
  "/checkout",
  "/checkouts",
  "/ops",
  "/pay",
] as const;

function canonicalPath(pathname: string) {
  if (pathname === "/") return "/";
  if ((pathname.split("/").pop() ?? "").includes(".")) return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isDetailBelowPrefix(pathname: string, prefix: string) {
  const normalizedPrefix = canonicalPath(prefix);
  const normalizedPath = canonicalPath(pathname);
  return normalizedPath !== normalizedPrefix && normalizedPath.startsWith(normalizedPrefix);
}

export function isPrivateCrawlPath(pathname: string) {
  return PRIVATE_CRAWL_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/**
 * Public utility/redirect surfaces that bots may follow through but must not
 * index. Detail pages below these hubs remain independently indexable.
 */
export function isPublicNoindexPath(pathname: string) {
  const normalized = canonicalPath(pathname);
  if (PUBLIC_NOINDEX_PATHS.has(normalized)) return true;
  return PUBLIC_NOINDEX_PATTERNS.some((pattern) => pattern.test(normalized));
}

export function isWeakIndexableValuePath(pathname: string) {
  return WEAK_INDEXABLE_VALUE_DETAIL_PREFIXES.some((prefix) =>
    isDetailBelowPrefix(pathname, prefix)
  );
}

export function isQueryCanonicalizationExemptPath(pathname: string) {
  return QUERY_CANONICAL_EXEMPT_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function legacyCrawlCanonicalPath(pathname: string) {
  return (
    LEGACY_CRAWL_CANONICAL_PATHS[pathname] ??
    LEGACY_CRAWL_CANONICAL_PATHS[canonicalPath(pathname)] ??
    null
  );
}

export function legacyPolicyCanonicalPath(pathname: string) {
  const canonical = legacyCrawlCanonicalPath(pathname);
  return pathname.startsWith("/policies/") ? canonical : null;
}

export function shouldInjectRouteJsonLd(pathname: string) {
  const normalized = canonicalPath(pathname);
  return !isPrivateCrawlPath(normalized) && !isPublicNoindexPath(normalized);
}
